import { Badge, Button, Col, message, Row, Typography } from "antd";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getInfluencers, searchInvitedBusiness } from "services/SauceService";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
// import { SubscriptionType } from "enums/SubscriptionType";
import { BusinessState, getFavoritedCreators } from "redux-context/business";
import CreatorCard from "./CreatorCard";
import Loader from "components/loader/Loader";
import AppliedFIlterChips, { FilterChip } from "./AppliedFIlterChips";
import FilterAI, { buildQuery } from "./FilterAI";
import DropdownFilterWrapper from "components/dropdown-filter-wrapper/DropdownFilterWrapper";
import {
  faArrowDownShortWide,
  faHashtag,
  faLocationDot,
  faTags,
  faUsersViewfinder,
  faVenusMars,
  faCheckCircle,
  faTag,
  faCalendarDays,
} from "@fortawesome/free-solid-svg-icons";
import AudienceFilter from "components/audience-filter/AudienceFilter";
import CategoriesTreeFilter from "components/categories-tree-filter/CategoriesTreeFilter";
import GenderFilter from "components/gender-filter/GenderFilter";
import InfluencersSort from "components/influencers-sort/InfluencersSort";
import LocationFilter from "components/location-filter/LocationFilter";
import MinimumThresholds from "components/minimum-thresholds/MinimumThresholds";
import PlatformFilter from "components/platform-filter/PlatformFilter";
import InvitationsTable from "components/invitations-table";
import StatusFilter from "components/status-filter/StatusFilter";
import InviteTypeFilter from "components/invite-type-filter/InviteTypeFilter";
import DateRangeFilter from "components/date-range-filter/DateRangeFilter";
import Applications from "pages/applications/Applications";
import { CollabsState } from "redux-context/collabs";
import { usePaywall } from "components/paywall/paywallContext";
import FakeCreatorCard from "./FakeCreatorCard";
import PaywallPopup from "components/paywall/PaywallPopup";
import { PaywallSource } from "components/paywall/Paywall";

enum CreatorTab {
  Creators = "CREATORS",
  Applied = "APPLIED",
  Invited = "INVITED",
  Saved = "SAVED",
}

const getFiltersFromURL = (search: string) => {
  const params = new URLSearchParams(search);
  const filter: any = {
    skip: 0,
    limit: 24,
  };
  if (params.has("minAmountFollowers")) {
    filter.minAmountFollowers = Number(params.get("minAmountFollowers"));
  }
  if (params.has("minAmountEngagement")) {
    filter.minAmountEngagement = Number(params.get("minAmountEngagement"));
  }
  if (params.has("categories")) {
    filter.categories = params.get("categories")?.split(";");
  }
  if (params.has("locations")) {
    filter.locations = params.get("locations")?.split(";");
  }
  if (params.has("gender")) {
    filter.gender = params.get("gender")?.split(";");
  }
  if (params.has("socialMediaName")) {
    filter.socialMediaName = params.get("socialMediaName");
  }
  if (params.has("audiences")) {
    filter.audiences = params.get("audiences")?.split(";");
  }
  if (params.has("minAmountEngagement") && !filter.minAmountEngagement) {
    filter.minAmountEngagement = Number(params.get("minAmountEngagement"));
  }
  if (params.has("socialMediaName")) {
    filter.socialMediaName = params.get("socialMediaName");
  }
  if (params.has("sortField")) {
    filter.sortField = params.get("sortField");
  }
  if (params.has("sortDirection")) {
    filter.sortDirection = params.get("sortDirection");
  }
  if (params.has("targetUser")) {
    filter.targetUser = params.get("targetUser");
  }
  return filter;
};

const getAppliedFilters = (newFieldValues: any) => {
  if (!newFieldValues) {
    return [];
  }

  const chips: FilterChip[] = [];

  if (
    newFieldValues.dateRange &&
    newFieldValues.dateRange.startDate &&
    newFieldValues.dateRange.endDate
  ) {
    chips.push({
      key: "dateRange",
      value: newFieldValues.dateRange, // Keep as object for proper formatting
    });
  }

  if (newFieldValues.inviteType && newFieldValues.inviteType.trim() !== "") {
    const inviteTypes = newFieldValues.inviteType
      .split(";")
      .map((type: string) => type.trim())
      .filter((type: string) => type !== "");

    inviteTypes.forEach((type: string) => {
      chips.push({
        key: "inviteType",
        value: type,
      });
    });
  }

  if (newFieldValues.status && newFieldValues.status.trim() !== "") {
    const statuses = newFieldValues.status
      .split(";")
      .map((status: string) => status.trim())
      .filter((status: string) => status !== "");

    statuses.forEach((status: string) => {
      chips.push({
        key: "status",
        value: status,
      });
    });
  }
  return chips;
};

// const hasUnpermittedFilters = (location: any) => {
//   const params = new URLSearchParams(location.search);
//   const keys = Array.from(params.keys());

//   if (keys.length > 1) {
//     return true;
//   }

//   return keys.length === 1 && keys[0] !== "locations";
// };

const CreatorsTab: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { openPaywall } = usePaywall();
  const [filter, setFilter] = useState(getFiltersFromURL(location.search));
  const [localLoading, setLocalLoading] = useState<boolean>(false);
  const [showPopupPaywall, setShowPopupPaywall] = useState<boolean>(false);
  const [fieldValues, setFieldValues] = useState<any>({
    ...getFiltersFromURL(location.search),
  });
  const [influencers, setInfluencers] = useState<any>({ data: [], total: 0 });
  const [totalInfluencers, setTotalInfluencers] = useState<number>(0);

  const buildFakeCreators = ({
    count,
    images = [],
    idPrefix = "fake",
  }: {
    count: number;
    images?: string[];
    idPrefix?: string;
  }) => {
    const safeCount = Math.max(1, count);

    return Array.from({ length: safeCount }).map((_, idx) => {
      const start = idx * 3;
      const top3PostsCoverImgUrl = Array.from({ length: 3 }).map(
        (__, imageIdx) => {
          if (images.length === 0) {
            return "";
          }

          const candidate = images[start + imageIdx];
          if (candidate) {
            return candidate;
          }

          return images[(start + imageIdx) % images.length] || "";
        },
      );

      return {
        foodieId: `${idPrefix}_${idx}`,
        collabId: `${idPrefix}_collab_${idx}`,
        name: `Creator ${idx + 1}`,
        username: `creator_${idx + 1}`,
        avatarUrl: "",
        top3PostsCoverImgUrl,
        instagramFollowersCount: 12000 + idx * 2500,
        tiktokFollowersCount: 9000 + idx * 2000,
        categoriesIA: ["food", "lifestyle", "local"],
        tabStatus: "pending",
        inviteAvailable: false,
        __isFake: true,
      };
    });
  };

  const { business } = useSelector(
    (state: BusinessState) => state.business,
  );
  // const freemium = subscription?.subscriptionType === SubscriptionType.Fremium;
  const freemium = false;
  // const blocked = freemium && hasUnpermittedFilters(location);
  const blocked = false;
  const onClickFoodie = (foodieId: string) => {
    navigate(`/creators/${foodieId}`);
  };

  const onClickFilter = () => {
    const queryString = buildQuery(fieldValues).join("&");
    navigate(`/creators?${queryString}`);
  };

  const getAllInfluencers = useCallback(
    async (filterArg?: any) => {
      if (!business._id) return;
      const currentFilter = filterArg ?? filter;
      const requestId = ++lastRequestIdRef.current;
      try {
        setLocalLoading(true);
        const params = {
          ...currentFilter,
          businessId: business._id,
        };
        const response: any = await getInfluencers(params);
        const responseData = Array.isArray(response?.data) ? response.data : [];
        const responseTotal =
          typeof response?.total === "number" ? response.total : 0;

        if (lastRequestIdRef.current !== requestId) {
          return;
        }

        const imagesLocal = responseData
          .map(
            (influencer: { top3PostsCoverImgUrl: string[] }) =>
              influencer.top3PostsCoverImgUrl,
          )
          .flat();

        if (freemium && blocked) {
          const cardsCount =
            imagesLocal.length > 0 ? Math.ceil(imagesLocal.length / 3) : 1;
          setInfluencers({
            data: buildFakeCreators({
              count: cardsCount,
              images: imagesLocal,
              idPrefix: "fake",
            }),
          });
        } else if (
          currentFilter.skip === 0 &&
          responseTotal > 0 &&
          responseData.length === 0
        ) {
          const fallbackCount = Math.min(
            currentFilter.limit || 24,
            responseTotal,
          );
          setInfluencers({
            data: buildFakeCreators({
              count: fallbackCount,
              idPrefix: "fallback",
            }),
            total: responseTotal,
          });
        } else {
          setInfluencers((prev: any) => {
            if (currentFilter.skip === 0) {
              return { ...prev, ...response, data: responseData };
            } else {
              return {
                ...prev,
                ...response,
                data: [...prev.data, ...responseData],
              };
            }
          });
        }
        setTotalInfluencers(responseTotal);
        setLocalLoading(false);
      } catch (error) {
        // ignore errors from stale requests
        if (lastRequestIdRef.current !== requestId) {
          return;
        }
        setInfluencers({ data: [], total: 0 });
        setTotalInfluencers(0);
        setLocalLoading(false);
        message.error("Error fetching creators");
      }
    },
    [filter, blocked, freemium, business],
  );

  // keep a ref to the last request id to ignore stale responses
  const lastRequestIdRef = useRef(0);

  useEffect(() => {
    const newFilter = getFiltersFromURL(location.search);
    setFieldValues(() => ({ ...newFilter }));
    setFilter(() => ({ ...newFilter }));
    setShowPopupPaywall(false);
  }, [location.search]);

  useEffect(() => {
    getAllInfluencers();
  }, [filter, getAllInfluencers]);

  useEffect(() => {
    const handleScroll = () => {
      const nearBottom =
        Math.abs(
          window.innerHeight +
          document.documentElement.scrollTop -
          document.documentElement.offsetHeight,
        ) < 10;

      if (nearBottom && !localLoading) {
        if (freemium) {
          setShowPopupPaywall(true);
          return;
        }
        setFilter((prevFilter: any) => {
          const nextSkip = prevFilter.skip + prevFilter.limit;
          if (nextSkip < totalInfluencers) {
            const nextFilter = {
              ...prevFilter,
              skip: nextSkip,
            };
            // fetch using the calculated nextFilter to avoid race with state updates
            getAllInfluencers(nextFilter);
            return nextFilter;
          }
          return prevFilter;
        });
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, [localLoading, totalInfluencers, freemium, getAllInfluencers]);

  useEffect(() => {
    if (blocked) {
      setShowPopupPaywall(true);
    }
  }, [blocked]);

  return (
    <>
      {showPopupPaywall && (
        <PaywallPopup
          onUpgrade={() => openPaywall({ source: PaywallSource.Creators })}
          totalInfluencers={totalInfluencers}
        />
      )}
      <Loader visible={localLoading} />
      <Row style={{ gap: 10, marginBottom: 20 }}>
        <DropdownFilterWrapper
          btnText="Location"
          btnIcon={faLocationDot}
          title="Location Filter"
          subtitle="Search by country, state/province/county, and city"
          onFilter={onClickFilter}
          onClear={() =>
            setFieldValues((prev: any) => ({ ...prev, locations: [] }))
          }
        >
          <LocationFilter
            onChangeValues={(val: any) =>
              setFieldValues((prev: any) => ({ ...prev, locations: val }))
            }
            values={fieldValues?.locations}
          />
        </DropdownFilterWrapper>
        <DropdownFilterWrapper
          btnText="Platform"
          btnIcon={faHashtag}
          title="Platform Filter"
          subtitle="Select social media platforms"
          onFilter={onClickFilter}
          onClear={() =>
            setFieldValues((prev: any) => ({ ...prev, socialMediaName: "" }))
          }
        >
          <PlatformFilter
            onChangeValues={(val: any) => {
              console.log(val);
              setFieldValues((prev: any) => ({
                ...prev,
                socialMediaName: val,
              }));
            }}
            values={fieldValues?.socialMediaName}
          />
        </DropdownFilterWrapper>
        <DropdownFilterWrapper
          btnText="Min"
          btnIcon={faHashtag}
          title="Minimum Thresholds"
          subtitle="Set minimum follower count and engagement rate"
          onFilter={onClickFilter}
          onClear={() =>
            setFieldValues((prev: any) => ({
              ...prev,
              minAmountFollowers: null,
              minAmountEngagement: null,
            }))
          }
        >
          <MinimumThresholds
            onChangeValues={(val: any) =>
              setFieldValues((prev: any) => ({ ...prev, ...val }))
            }
            values={{
              minAmountFollowers: fieldValues?.minAmountFollowers,
              minAmountEngagement: fieldValues?.minAmountEngagement,
            }}
          />
        </DropdownFilterWrapper>
        <DropdownFilterWrapper
          btnText="Categories"
          btnIcon={faTags}
          title="Categories Filter"
          subtitle="Search and select creator categories"
          onFilter={onClickFilter}
          onClear={() =>
            setFieldValues((prev: any) => ({ ...prev, categories: "" }))
          }
        >
          <CategoriesTreeFilter
            onChangeValues={(val: any) =>
              setFieldValues((prev: any) => ({
                ...prev,
                categories: val,
              }))
            }
            values={fieldValues?.categories}
            numColumns={4}
          />
        </DropdownFilterWrapper>
        <DropdownFilterWrapper
          btnText="Audiences"
          btnIcon={faUsersViewfinder}
          title="Audience Demographics"
          subtitle="Select target audience age groups and gender"
          onFilter={onClickFilter}
          onClear={() =>
            setFieldValues((prev: any) => ({ ...prev, audiences: "" }))
          }
        >
          <AudienceFilter
            onChangeValues={(val: any) =>
              setFieldValues((prev: any) => ({ ...prev, audiences: val }))
            }
            values={fieldValues?.audiences}
          />
        </DropdownFilterWrapper>

        <DropdownFilterWrapper
          btnText="Gender"
          btnIcon={faVenusMars}
          title="Gender Filter"
          subtitle="Select preferred creator gender."
          onFilter={onClickFilter}
          onClear={() =>
            setFieldValues((prev: any) => ({ ...prev, gender: "" }))
          }
        >
          <GenderFilter
            onChangeValues={(val: any) =>
              setFieldValues((prev: any) => ({ ...prev, gender: val }))
            }
            values={fieldValues?.gender}
          />
        </DropdownFilterWrapper>

        <div style={{ marginLeft: "auto" }}>
          <DropdownFilterWrapper
            btnText="Sort"
            btnIcon={faArrowDownShortWide}
            title="Sort By"
            subtitle="Choose how to order creators"
            onFilter={onClickFilter}
            onClear={() =>
              setFieldValues((prev: any) => ({
                ...prev,
                sortField: undefined,
                sortDirection: undefined,
              }))
            }
          >
            <InfluencersSort
              onChangeValues={(val: any) =>
                setFieldValues((prev: any) => ({ ...prev, ...val }))
              }
              values={{
                sortField: fieldValues?.sortField,
                sortDirection: fieldValues?.sortDirection,
              }}
            />
          </DropdownFilterWrapper>
        </div>
      </Row>

      <Row>
        <Col xs={24} className="container-amount-creators">
          <Typography.Text>
            {totalInfluencers?.toLocaleString()} creators found
          </Typography.Text>
          <Button
            className={`clear-all-button ${location.search.length > 0 ? "visible" : "hidden"
              }`}
            type="link"
            onClick={() => navigate("/creators")}
          >
            Clear all filters
          </Button>
        </Col>
      </Row>
      <AppliedFIlterChips />
      <Row
        style={{
          gap: 12,
          marginTop: location.search ? 10 : 16,
          padding: "0 4px",
        }}
      >
        {influencers.data?.map((influencer: any, idx: number) => {
          const isMockCard = blocked || influencer?.__isFake;

          if (isMockCard) {
            return (
              <FakeCreatorCard
                creator={influencer}
                key={`INFLUENCER_${influencer.foodieId}_${influencer.collabId}_${idx}`}
                index={idx}
                onClick={
                  blocked
                    ? () =>
                      openPaywall({
                        source: PaywallSource.Creators,
                        onSubscribe: () => {
                          window.location.reload();
                        },
                      })
                    : undefined
                }
              />
            );
          }

          return (
            <CreatorCard
              creator={influencer}
              onInfluencerClick={() => onClickFoodie(influencer.foodieId)}
              key={`INFLUENCER_${influencer.foodieId}_${influencer.collabId}_${idx}`}
              index={idx}
            />
          );
        })}
      </Row>
    </>
  );
};

const SavedTab: React.FC = () => {
  const navigate = useNavigate();
  const { favoritedCreators } = useSelector(
    (state: BusinessState) => state.business,
  );

  const onClickFoodie = (foodieId: string) => {
    navigate(`/creators/${foodieId}`);
  };

  return (
    <>
      <Row style={{ gap: 16, marginTop: 24 }}>
        {favoritedCreators?.map((influencer: any, idx: number) => (
          <CreatorCard
            creator={influencer}
            onInfluencerClick={() => onClickFoodie(influencer.foodieId)}
            key={`INFLUENCER_${influencer.foodieId}_${influencer.collabId}_${idx}`}
            index={idx}
          />
        ))}
      </Row>
    </>
  );
};

const InvitedTab: React.FC = () => {
  const [localLoading, setLocalLoading] = useState<boolean>(false);
  const [totalInvitations, setTotalInvitations] = useState<number>(0);
  const [fieldValues, setFieldValues] = useState<any>({});
  const [appliedFilters, setAppliedFilters] = useState<FilterChip[]>([]);
  const [appliedFilterValues, setAppliedFilterValues] = useState<any>({});

  const fetchInvitations = useCallback(
    async (limit: number, offset: number) => {
      setLocalLoading(true);
      try {
        const businessId = localStorage.getItem("businessId");
        if (!businessId) {
          return { data: [], total: 0 };
        }

        const apiParams: any = {
          businessId,
          limit,
          offset,
        };

        if (
          appliedFilterValues.dateRange &&
          appliedFilterValues.dateRange.startDate &&
          appliedFilterValues.dateRange.endDate
        ) {
          apiParams.startDate = dayjs(appliedFilterValues.dateRange.startDate)
            .startOf("day")
            .toISOString();
          apiParams.endDate = dayjs(appliedFilterValues.dateRange.endDate)
            .endOf("day")
            .toISOString();
        }

        if (
          appliedFilterValues.inviteType &&
          appliedFilterValues.inviteType.trim() !== ""
        ) {
          const inviteTypes = appliedFilterValues.inviteType
            .split(";")
            .map((type: string) => type.trim())
            .filter((type: string) => type !== "");
          if (inviteTypes.length > 0) {
            apiParams.inviteType = inviteTypes;
          }
        }

        if (
          appliedFilterValues.status &&
          appliedFilterValues.status.trim() !== ""
        ) {
          const statuses = appliedFilterValues.status
            .split(";")
            .map((status: string) => status.trim())
            .filter((status: string) => status !== "");
          if (statuses.length > 0) {
            apiParams.status = statuses;
          }
        }

        const response: any = await searchInvitedBusiness(apiParams);
        setTotalInvitations(response?.pagination?.total || 0);
        return {
          data: response?.data || [],
          total: response?.pagination?.total || 0,
        };
      } catch (err) {
        console.error("Failed to fetch invitations", err);
        return { data: [], total: 0 };
      } finally {
        setLocalLoading(false);
      }
    },
    [appliedFilterValues],
  );

  const handleRemoveFilter = useCallback((filter: FilterChip) => {
    setAppliedFilterValues((prev: any) => {
      const updated = { ...prev };

      if (filter.key === "dateRange") {
        updated.dateRange = null;
      } else if (filter.key === "inviteType") {
        if (updated.inviteType) {
          const filteredTypes = updated.inviteType
            .split(";")
            .map((type: string) => type.trim())
            .filter((type: string) => type !== filter.value && type !== "");

          updated.inviteType =
            filteredTypes.length > 0 ? filteredTypes.join(";") : null;
        }
      } else if (filter.key === "status") {
        if (updated.status) {
          const filteredStatuses = updated.status
            .split(";")
            .map((status: string) => status.trim())
            .filter(
              (status: string) => status !== filter.value && status !== "",
            );

          updated.status =
            filteredStatuses.length > 0 ? filteredStatuses.join(";") : null;
        }
      }

      setAppliedFilters(getAppliedFilters(updated));
      return updated;
    });
  }, []);

  const onClickFilter = () => {
    setAppliedFilterValues(fieldValues);
    setAppliedFilters(getAppliedFilters(fieldValues));
  };

  return (
    <>
      <Row>
        <Col xs={24} style={{ gap: 10, display: "flex" }}>
          <DropdownFilterWrapper
            btnText="Date Range"
            btnIcon={faCalendarDays}
            title="Filter by Date Range"
            subtitle="Select when invites were sent"
            onFilter={onClickFilter}
            onClear={() =>
              setFieldValues((prev: any) => ({ ...prev, dateRange: null }))
            }
          >
            <DateRangeFilter
              values={fieldValues?.dateRange}
              onChangeValues={(val: any) =>
                setFieldValues((prev: any) => ({ ...prev, dateRange: val }))
              }
            />
          </DropdownFilterWrapper>
          <DropdownFilterWrapper
            btnText="Invite type"
            btnIcon={faTag}
            title="Filter by Invite Type"
            subtitle="Select invitation method"
            onFilter={onClickFilter}
            onClear={() =>
              setFieldValues((prev: any) => ({ ...prev, inviteType: null }))
            }
          >
            <InviteTypeFilter
              onChangeValues={(val: any) =>
                setFieldValues((prev: any) => ({ ...prev, inviteType: val }))
              }
              values={fieldValues?.inviteType}
            />
          </DropdownFilterWrapper>
          <DropdownFilterWrapper
            btnText="Status"
            btnIcon={faCheckCircle}
            title="Filter by Status"
            subtitle="Select invitation status"
            onFilter={onClickFilter}
            onClear={() =>
              setFieldValues((prev: any) => ({ ...prev, status: null }))
            }
          >
            <StatusFilter
              onChangeValues={(val: any) =>
                setFieldValues((prev: any) => ({ ...prev, status: val }))
              }
              values={fieldValues?.status}
            />
          </DropdownFilterWrapper>
        </Col>
      </Row>
      <Row>
        <Col xs={24}>
          <h1 className="header-2" style={{ marginBottom: 0, marginTop: 24 }}>
            Invited Creators
          </h1>
          <p style={{ marginTop: 0, color: "#999" }}>
            {totalInvitations.toLocaleString()} creators invited
          </p>
        </Col>
      </Row>

      <AppliedFIlterChips
        filters={appliedFilters}
        onRemoveFilter={handleRemoveFilter}
      />

      <Row>
        <Col xs={24} style={{ marginTop: 24 }}>
          <InvitationsTable
            fetch={fetchInvitations}
            showCreatorInfo={true}
            loading={localLoading}
          />
        </Col>
      </Row>
    </>
  );
};

const Influencers: React.FC = () => {
  const dispatch = useDispatch<any>();
  const [selectedTab, setSelectedTab] = useState<CreatorTab>(
    CreatorTab.Creators,
  );
  const { favoritedCreators } = useSelector(
    (state: BusinessState) => state.business,
  );
  const { numbers } = useSelector((state: CollabsState) => state.collabs);

  useEffect(() => {
    dispatch(getFavoritedCreators());
  }, [dispatch]);

  return (
    <div className="influencers">
      <Row justify="space-between">
        <FilterAI />
      </Row>

      <div className="creator-tabs">
        <button
          className={`creator-tab${selectedTab === CreatorTab.Creators ? " creator-tab--active" : ""
            }`}
          onClick={() => setSelectedTab(CreatorTab.Creators)}
        >
          All creators
        </button>
        <button
          className={`creator-tab${selectedTab === CreatorTab.Applied ? " creator-tab--active" : ""
            }`}
          onClick={() => setSelectedTab(CreatorTab.Applied)}
        >
          Applied
          <Badge
            count={numbers.pending}
            color="#ffac39"
            style={{ color: "#1D2023", fontWeight: 700, padding: "0 8px" }}
          />
        </button>

        <button
          className={`creator-tab${selectedTab === CreatorTab.Applied ? " creator-tab--active" : ""
            }`}
          onClick={() => setSelectedTab(CreatorTab.Applied)}
          style={{ display: "none" }}
        >
          Applied <span className="creator-tab__badge">24</span>
        </button>
        <button
          className={`creator-tab${selectedTab === CreatorTab.Invited ? " creator-tab--active" : ""
            }`}
          onClick={() => setSelectedTab(CreatorTab.Invited)}
        >
          Invited
        </button>
        <button
          className={`creator-tab${selectedTab === CreatorTab.Saved ? " creator-tab--active" : ""
            }`}
          onClick={() => setSelectedTab(CreatorTab.Saved)}
        >
          Saved{" "}
          <span className="creator-tab__badge creator-tab__badge--gray">
            {favoritedCreators?.length}
          </span>
        </button>
      </div>

      {selectedTab === CreatorTab.Creators && <CreatorsTab />}

      {selectedTab === CreatorTab.Saved && <SavedTab />}

      {selectedTab === CreatorTab.Invited && <InvitedTab />}

      {selectedTab === CreatorTab.Applied && <Applications />}
    </div>
  );
};

export default Influencers;
